/**
 * RelatedItemCard - Reusable card component for displaying related research items
 * Used in all sections of the Issue Resolution Canvas
 */

import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Link2 } from 'lucide-react';
import { getStrengthBgColor, getStrengthColor } from './hooks/useIssueRelationships';

interface RelatedItemCardProps {
  title: string;
  description?: string;
  type: 'Trend' | 'Hypothesis' | 'BestPractice' | 'DataElement';
  relationship: {
    type: string;
    strength: string;
    explanation: string;
  };
  children?: React.ReactNode;
  accentColor: string;
  borderColor: string;
  bgColor: string;
}

// Color mapping for node types
export const NODE_COLORS = {
  Trend: {
    accent: 'text-blue-600',
    border: 'border-blue-200',
    bg: 'bg-blue-50',
    badge: 'bg-blue-100 text-blue-700',
  },
  Issue: {
    accent: 'text-red-600',
    border: 'border-red-200',
    bg: 'bg-red-50',
    badge: 'bg-red-100 text-red-700',
  },
  BestPractice: {
    accent: 'text-green-600',
    border: 'border-green-200',
    bg: 'bg-green-50',
    badge: 'bg-green-100 text-green-700',
  },
  DataElement: {
    accent: 'text-purple-600',
    border: 'border-purple-200',
    bg: 'bg-purple-50',
    badge: 'bg-purple-100 text-purple-700',
  },
  Hypothesis: {
    accent: 'text-amber-600',
    border: 'border-amber-200',
    bg: 'bg-amber-50',
    badge: 'bg-amber-100 text-amber-700',
  },
};

// Relationship type display names
const RELATIONSHIP_DISPLAY: Record<string, string> = {
  DRIVES: 'Drives',
  ADDRESSED_BY: 'Addressed by',
  INVESTIGATES: 'Investigates',
  REQUIRES: 'Requires',
  VALIDATES: 'Validates',
  ACCELERATES: 'Accelerates',
  MITIGATES: 'Mitigates',
  ENABLES: 'Enables',
};

export const RelatedItemCard: React.FC<RelatedItemCardProps> = ({
  title,
  description,
  type,
  relationship,
  children,
  accentColor,
  borderColor,
  bgColor,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div
      className={`rounded-lg border ${borderColor} ${bgColor} overflow-hidden transition-all duration-200 hover:shadow-sm`}
    >
      <div
        className="p-3 cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <h4 className={`font-medium ${accentColor} text-sm truncate`}>
              {title}
            </h4>
            {description && !isExpanded && (
              <p className="text-xs text-neutral-600 mt-1 line-clamp-2">
                {description}
              </p>
            )}
          </div>
          <button
            className="p-1 hover:bg-white/50 rounded transition-colors flex-shrink-0"
            aria-label={isExpanded ? 'Collapse' : 'Expand'}
          >
            {isExpanded ? (
              <ChevronUp className="w-4 h-4 text-neutral-400" />
            ) : (
              <ChevronDown className="w-4 h-4 text-neutral-400" />
            )}
          </button>
        </div>

        {/* Relationship indicator */}
        <div className="flex items-center gap-2 mt-2">
          <Link2 className={`w-3 h-3 ${getStrengthColor(relationship.strength)}`} />
          <span className="text-xs text-neutral-500">
            {RELATIONSHIP_DISPLAY[relationship.type] || relationship.type}
          </span>
          <span
            className={`text-xs px-1.5 py-0.5 rounded ${getStrengthBgColor(relationship.strength)}`}
          >
            {relationship.strength}
          </span>
        </div>
      </div>

      {/* Expanded content */}
      {isExpanded && (
        <div className="px-3 pb-3 border-t border-white/50">
          {description && (
            <p className="text-sm text-neutral-700 mt-3">{description}</p>
          )}

          {/* Relationship explanation */}
          <div className="mt-3 p-2 bg-white/50 rounded text-xs text-neutral-600">
            <span className="font-medium">Relationship: </span>
            {relationship.explanation}
          </div>

          {/* Additional content specific to item type */}
          {children && <div className="mt-3">{children}</div>}
        </div>
      )}
    </div>
  );
};

/**
 * Compact card variant for horizontal display
 */
interface CompactItemCardProps {
  title: string;
  type: 'Trend' | 'Hypothesis' | 'BestPractice' | 'DataElement';
  strength: string;
  onClick?: () => void;
}

export const CompactItemCard: React.FC<CompactItemCardProps> = ({
  title,
  type,
  strength,
  onClick,
}) => {
  const colors = NODE_COLORS[type];

  return (
    <div
      className={`px-3 py-2 rounded-lg border ${colors.border} ${colors.bg} cursor-pointer hover:shadow-sm transition-all`}
      onClick={onClick}
    >
      <span className={`text-sm font-medium ${colors.accent} line-clamp-1`}>
        {title}
      </span>
      <div className="flex items-center gap-1 mt-1">
        <span
          className={`text-xs px-1.5 py-0.5 rounded ${getStrengthBgColor(strength)}`}
        >
          {strength}
        </span>
      </div>
    </div>
  );
};

export default RelatedItemCard;
